import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AmityUiKitProvider } from '@frn/amityco-ui-kit-open-source';
import jwt_decode from 'jwt-decode';

import { actions } from 'modules/user';
import ProductSidebar from 'components/ProductSidebar';
import FloatingChat from 'components/VirtualMentorChat/components/FloatingChat';

import { TrackProvider, useTrackData, useTrackListData } from './contexts/TrackContext';
import { WeekProvider, useWeekContentData } from './contexts/WeekContext';
import { WLCProvider } from './contexts/WLCContext';
import WLCNav from './components/WLCNav';
import WLCRoutes from './components/WLCRoutes';
import DebugProgressPopup from './components/DebugProgressPopup';
import { useWLCData } from './hooks';
import { getProductFromPath, getContentSlugFromPath, getDisplayName, buildProductPath } from './utils';
import { NAV_ITEMS, SOCIAL_PLUS_API_KEY, AMITY_CONFIGS, FLOATING_CHAT_SUGGESTIONS, PRODUCT_SLUG } from './constants';

// ─── Redux wiring ───────────────────────────────────────────────────
const mapStateToProps = state => ({
  user: state.user
});

const mapDispatchToProps = {
  refreshToken: actions.refreshToken,
  retrieveSocialPlusToken: actions.retrieveSocialPlusToken,
  getUserData: actions.getUserData,
  postUserData: actions.postUserData
};

// ─── Component ──────────────────────────────────────────────────────
const WLC = ({ user, refreshToken, retrieveSocialPlusToken, getUserData, postUserData }) => {
  const history = useHistory();
  const location = useLocation();
  const productSlug = getProductFromPath();
  const contentSlug = getContentSlugFromPath();

  const isAdminUser = useMemo(() => {
    const decodedToken = jwt_decode(user?.authToken);
    return decodedToken && decodedToken.role === 'admin';
  }, [user?.authToken]);

  const shouldShowDebugProgressPopup = useMemo(() => {
    const searchParams = new URLSearchParams(location?.search || '');
    return searchParams.get('debug') === '1' && isAdminUser;
  }, [location?.search, isAdminUser]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // ── Navigation ──────────────────────────────────────────────────
  const handleNav = useCallback(
    (name, typeOfComponent, slug) => {
      console.log('handleNav', name, typeOfComponent, slug);
      const _typeOfComponent = ['link', 'download'].includes(typeOfComponent) ? 'ExternalLink' : typeOfComponent;
      if (_typeOfComponent === 'ExternalLink') {
        window.open(slug, '_blank').focus();
        return;
      }
      if (_typeOfComponent === 'InternalLink') {
        history.push(`${slug.startsWith('/') ? '' : '/'}${slug}${slug.endsWith('/') ? '' : '/'}`, {});
        return;
      }
      history.push(buildProductPath(productSlug, slug), {});
    },
    [history, productSlug]
  );

  // ── User state (tours, onboarding) via custom hook ──────────────
  const {
    toursCompleted,
    isLegacyMember,
    isOnboard,
    loadingState,
    gettingStartedCompleted,
    wlcProgress,
    updateTourStatus,
    completeOnboarding,
    updateWlcProgress
  } = useWLCData({
    getUserData,
    postUserData,
    refreshToken,
    handleNav
  });

  // ── Amity social auth ───────────────────────────────────────────
  const getAuthToken = useCallback(async () => {
    const token = await retrieveSocialPlusToken(PRODUCT_SLUG);
    return token;
  }, [retrieveSocialPlusToken]);

  // ── Fetch current track content from user progress ──────────────
  const { track: currentTrack, isLoading: isTrackLoading } = useTrackData(
    wlcProgress?.currentTrack,
    PRODUCT_SLUG,
    user.authToken,
    { depth: 2, enabled: !loadingState && !!wlcProgress?.currentTrack }
  );

  // ── Fetch current track content from user progress ──────────────
  const { trackList, isLoading: isTrackListLoading } = useTrackListData(PRODUCT_SLUG, user.authToken, {
    depth: 2,
    enabled: !loadingState
  });

  // ── Fetch current week content from user progress ───────────────
  const { week: currentWeek, isLoading: isWeekLoading } = useWeekContentData(
    wlcProgress?.currentWeekSlug,
    PRODUCT_SLUG,
    user.authToken,
    { enabled: !loadingState && !!wlcProgress?.currentWeekSlug }
  );

  // ── Derived user display values ─────────────────────────────────
  const { userId, firstName, lastName } = user;
  const userIdString = useMemo(() => String(userId), [userId]);
  const displayName = useMemo(() => getDisplayName(firstName, lastName), [firstName, lastName]);

  // ── Hide medical disclaimer on community pages ──────────────────
  const showDisclaimer = contentSlug !== 'community';
  console.log('CURRENT TRACK:', currentTrack);
  console.log('CURRENT WEEK:', currentWeek);
  return (
    <AmityUiKitProvider
      key={userId}
      apiKey={SOCIAL_PLUS_API_KEY}
      userId={userIdString}
      displayName={displayName}
      apiRegion="US"
      getAuthToken={getAuthToken}
      configs={AMITY_CONFIGS}
    >
      <WLCProvider
        toursCompleted={toursCompleted}
        isOnboard={isOnboard}
        loadingState={loadingState}
        completeOnboarding={completeOnboarding}
        gettingStartedCompleted={gettingStartedCompleted}
        wlcProgress={wlcProgress}
        updateTourStatus={updateTourStatus}
        updateWlcProgress={updateWlcProgress}
        handleNav={handleNav}
        productSlug={productSlug}
        getAuthToken={getAuthToken}
        authToken={user.authToken}
      >
        <TrackProvider initialTrack={currentTrack} initialTrackList={trackList}>
          <WeekProvider initialWeek={currentWeek}>
            <ProductSidebar navItems={NAV_ITEMS} handleNav={handleNav} hideNav={!isOnboard}>
              <div className="wlcContainer">
                <WLCNav />
                {isOnboard ? (
                  <FloatingChat
                    suggestions={FLOATING_CHAT_SUGGESTIONS}
                    suggestionsTitle="Suggested Prompts"
                    showSuggestionsWhenEmpty
                    maxSuggestions={4}
                    streaming
                    userId={userId}
                    disableLaunchModal={!currentTrack || currentTrack?.slug === 'wlc-track-introductory'}
                  />
                ) : null}
                {shouldShowDebugProgressPopup ? <DebugProgressPopup /> : null}
                <WLCRoutes productSlug={productSlug} />
              </div>
            </ProductSidebar>
          </WeekProvider>
        </TrackProvider>
      </WLCProvider>
    </AmityUiKitProvider>
  );
};

WLC.displayName = 'WLC';

export default connect(mapStateToProps, mapDispatchToProps)(WLC);
